/**
 * "Existing analysis found" prompt hook.
 *
 * Usage:
 *   const openAnalysis = useOpenAnalysis(nav);
 *   await openAnalysis("JPM");   // handles: reuse existing / offer refresh / block if duplicate job
 *
 * Behaviour:
 *   1. GET /analysis/existing?symbol=X
 *   2. If active_job exists → info dialog + open latest analysis
 *   3. If saved analysis exists → confirm dialog (Open Existing / Refresh / Cancel)
 *      - Open Existing → nav to /proposal/{id}, no AI call
 *      - Refresh → POST /analysis/refresh (background job) + nav to latest
 *   4. If no saved analysis → confirm the AI cost and run POST /analyze
 */
import { useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";

function humanAge(sec) {
  if (sec === null || sec === undefined) return "—";
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h`;
  return `${Math.round(sec / 86400)}d`;
}

export function useOpenAnalysis(nav) {
  const confirm = useConfirm();

  return useCallback(async (symbol) => {
    const sym = symbol.toUpperCase();
    let existing;
    try {
      existing = (await api.get("/analysis/existing", { params: { symbol: sym } })).data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to check existing analysis");
      return;
    }

    // 1) Refresh in-flight — offer to view latest or watch progress
    if (existing.active_job) {
      const openLatest = await confirm.confirm({
        variant: "info",
        title: `${sym} refresh already in progress`,
        description: `A fresh analysis is running (job ${existing.active_job.id.slice(0, 8)}). You can view the latest saved version while it completes.`,
        confirmLabel: existing.analysis ? "Open Existing Analysis" : "OK",
        cancelLabel: "Close",
        testId: "analysis-inflight-info",
      });
      if (openLatest && existing.analysis) nav(`/proposal/${existing.analysis.id}`);
      return;
    }

    // 2) Existing saved analysis — offer Reuse / Refresh
    if (existing.analysis) {
      const a = existing.analysis;
      const m = a.analysis_meta || {};
      const budget = existing.budget || {};
      const s = budget?.summary || {};
      const cfg = s.config || {};
      const remainingMonthly = Math.max(0, (cfg.monthly_per_user_usd || 0) - (s.user_month || 0));

      const chosen = await confirm.confirm({
        variant: "info",
        title: `Existing Analysis Found`,
        description: `A saved analysis for ${sym} already exists. Reopen it (no AI cost) or refresh with fresh data.`,
        details: {
          "analysis age": humanAge(a.freshness?.age_seconds),
          freshness: a.freshness?.status || "?",
          "previous verdict": m.verdict || "—",
          "price at analysis": `$${(m.price_at_analysis || 0).toFixed(2)}`,
          "estimated refresh cost": `~$${(existing.estimated_cost || 0).toFixed(3)}`,
          "monthly budget remaining": `$${remainingMonthly.toFixed(2)}`,
        },
        confirmLabel: "Refresh Full Analysis",
        cancelLabel: "Open Existing (no cost)",
        testId: "existing-analysis-prompt",
      });
      // Primary = Refresh, Cancel = Open Existing, X/ESC = Open Existing (safe default)
      if (chosen === false || chosen === null || chosen === undefined) {
        nav(`/proposal/${a.id}`);
        return;
      }
      if (chosen === true) {
        // Refresh selected — confirm budget impact
        if (!budget.allow) {
          await confirm.alert({
            variant: "error",
            title: "AI budget cap reached",
            description: budget.message || "Non-essential AI requests are blocked. Adjust caps in Settings → Cost or wait for the next period.",
            confirmLabel: "OK",
            testId: "budget-blocked-alert",
          });
          return;
        }
        try {
          const { data } = await api.post("/analysis/refresh", { symbol: sym, parent_analysis_id: a.id });
          if (data.duplicate) {
            toast.info(`Refresh for ${sym} already running — opening current analysis.`);
            nav(`/proposal/${a.id}`);
            return;
          }
          const jobId = data.job?.id;
          toast.success(`Refresh queued · job ${(jobId || "").slice(0, 8)}. Waiting for the fresh analysis…`);
          // Poll the job until completion (≤ 90 s @ 1.5 s cadence). When
          // it lands, fetch `/analysis/existing` again — the new analysis
          // id will be there — and navigate to THAT (not the stale one).
          // Previously the code navigated to `a.id` (the OLD analysis)
          // immediately, so users saw the pre-refresh page and thought
          // Refresh was a no-op.
          const deadline = Date.now() + 90_000;
          let landedAnalysisId = null;
          while (Date.now() < deadline) {
            const jr = await api.get(`/analysis/jobs/active`, { params: { symbol: sym } }).catch(() => null);
            const j = jr?.data?.active_job;
            const status = j?.status;
            if (!j || ["completed", "failed", "cancelled"].includes(status)) {
              const after = await api.get("/analysis/existing", { params: { symbol: sym } }).catch(() => null);
              landedAnalysisId = after?.data?.analysis?.id || null;
              break;
            }
            await new Promise((res) => setTimeout(res, 1500));
          }
          if (landedAnalysisId && landedAnalysisId !== a.id) {
            toast.success(`Fresh analysis ready for ${sym}`);
            nav(`/proposal/${landedAnalysisId}`);
          } else if (landedAnalysisId) {
            // Same id — refresh updated the existing record in place.
            nav(`/proposal/${landedAnalysisId}`);
          } else {
            toast.info(`Refresh still running — opening current analysis for now.`);
            nav(`/proposal/${a.id}`);
          }
        } catch (e) {
          const d = e?.response?.data?.detail;
          toast.error(typeof d === "string" ? d : d?.message || "Refresh failed");
        }
      }
      return;
    }

    // 3) No existing analysis — confirm the first-time AI cost then run
    const budget = existing.budget || {};
    if (!budget.allow) {
      await confirm.alert({
        variant: "error",
        title: "AI budget cap reached",
        description: budget.message || "Cannot run new AI analysis.",
        testId: "budget-blocked-alert",
      });
      return;
    }
    const ok = await confirm.confirm({
      variant: "budget",
      title: `Analyze ${sym}?`,
      description: "This will retrieve current market data and run the Trade Analyst + Risk Critic + Risk Engine.",
      details: {
        "estimated AI cost": `~$${(existing.estimated_cost || 0).toFixed(3)}`,
        "user monthly used": `$${(budget.summary?.user_month || 0).toFixed(4)}`,
      },
      confirmLabel: "Run Analysis",
      testId: "new-analysis-confirm",
    });
    if (!ok) return;
    try {
      const { data } = await api.post("/analyze", { symbol: sym });
      nav(`/proposal/${data.id}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Analysis failed");
    }
  }, [confirm, nav]);
}
